import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Paperclip, Plus, RefreshCw, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hhb_admin_token";
const imgSrc = (p) => (p && p.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${p}` : p);

const formatDetail = (d) => {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || String(d);
};

const fieldCls =
  "w-full border-0 border-b border-ink/20 bg-transparent px-0 py-2 text-sm text-ink outline-none transition-colors duration-300 focus:border-clay focus:ring-0";

const ProjectRow = ({ project, token, onSaved }) => {
  const [p, setP] = useState(project);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };
  const set = (k) => (e) => setP((prev) => ({ ...prev, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/projects/${p.id}`, {
        name: p.name, client: p.client, brief: p.brief, result: p.result,
        cat: p.cat, order: Number(p.order) || 0,
        handled: typeof p.handled === "string" ? p.handled.split(",").map((s) => s.trim()).filter(Boolean) : p.handled,
      }, { headers });
      toast.success(`Saved "${p.name}"`);
      onSaved();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await axios.post(`${API}/admin/projects/${p.id}/image`, fd, { headers });
      setP((prev) => ({ ...prev, image: data.image }));
      toast.success("Image replaced");
      onSaved();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`admin-project-${p.id}`} className="border border-ink/10 bg-white p-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
        <div className="md:col-span-2">
          <img src={imgSrc(p.image)} alt={p.name} className="aspect-[3/4] w-full max-w-28 object-cover" />
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" onChange={uploadImage} className="hidden" data-testid={`admin-project-file-${p.id}`} />
          <button
            onClick={() => fileRef.current?.click()}
            data-testid={`admin-project-replace-${p.id}`}
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:border-clay hover:text-clay"
          >
            <Upload className="h-3.5 w-3.5" />
            Replace image
          </button>
        </div>
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:col-span-10 md:grid-cols-2">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/40">Name</label>
            <input value={p.name} onChange={set("name")} className={fieldCls} data-testid={`admin-project-name-${p.id}`} />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/40">Client line</label>
            <input value={p.client} onChange={set("client")} className={fieldCls} />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/40">Category</label>
            <input value={p.cat} onChange={set("cat")} className={fieldCls} />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/40">Order</label>
            <input value={p.order} onChange={set("order")} type="number" className={fieldCls} />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/40">Brief</label>
            <textarea value={p.brief} onChange={set("brief")} rows={2} className={`${fieldCls} resize-none`} />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/40">Result</label>
            <input value={p.result} onChange={set("result")} className={fieldCls} />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/40">Handled (comma separated)</label>
            <input value={Array.isArray(p.handled) ? p.handled.join(", ") : p.handled} onChange={set("handled")} className={fieldCls} />
          </div>
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid={`admin-project-save-${p.id}`}
          className="rounded-full bg-ink px-7 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-bone transition-colors duration-300 hover:bg-clay disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState(null);
  const [projects, setProjects] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async (tok) => {
    try {
      const [{ data: enq }, { data: proj }] = await Promise.all([
        axios.get(`${API}/admin/enquiries`, { headers: { Authorization: `Bearer ${tok}` } }),
        axios.get(`${API}/projects`),
      ]);
      setEnquiries(enq);
      setProjects(proj);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const addProject = async () => {
    try {
      await axios.post(`${API}/admin/projects`, {
        name: "New garment", client: "Made for a client", cat: "Mixed",
        brief: "", result: "", handled: [], order: (projects?.length || 0) + 1,
      }, { headers });
      toast.success("Garment added");
      load(token);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bone px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md border border-ink/10 bg-white p-10">
          <div className="flex justify-center">
            <img src="/images/logo.png" alt="HHB Group" className="h-14 w-auto" />
          </div>
          <h1 className="mt-6 text-center font-serif text-3xl tracking-tight text-ink">Enquiries Admin</h1>
          <p className="mt-2 text-center text-sm text-ink/50">Private area — HHB Clothing Co only.</p>
          <form onSubmit={login} data-testid="admin-login-form" className="mt-8 flex flex-col gap-6">
            <div>
              <label htmlFor="admin-email" className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">Email</label>
              <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" className={fieldCls + " py-3"} />
            </div>
            <div>
              <label htmlFor="admin-password" className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">Password</label>
              <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" className={fieldCls + " py-3"} />
            </div>
            {error && <p data-testid="admin-login-error" className="text-sm text-red-700">{error}</p>}
            <button type="submit" disabled={loading} data-testid="admin-login-button" className="rounded-full bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-bone transition-colors duration-300 hover:bg-clay disabled:opacity-60">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-dashboard">
      <header className="border-b border-ink/10 bg-bone/90">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-5">
          <div className="flex items-center gap-3">
            <img src="/images/logo.png" alt="HHB Group" className="h-10 w-auto" />
            <span className="font-serif text-xl font-semibold tracking-tight text-ink">
              HHB <span className="italic text-clay">Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-full border border-ink/20 p-1">
              <button onClick={() => setTab("enquiries")} data-testid="admin-tab-enquiries" className={`rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${tab === "enquiries" ? "bg-ink text-bone" : "text-ink/60 hover:text-clay"}`}>
                Enquiries
              </button>
              <button onClick={() => setTab("content")} data-testid="admin-tab-content" className={`rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${tab === "content" ? "bg-ink text-bone" : "text-ink/60 hover:text-clay"}`}>
                Garments
              </button>
            </div>
            <button onClick={() => load(token)} data-testid="admin-refresh-button" aria-label="Refresh" className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/20 text-ink transition-colors duration-300 hover:border-clay hover:text-clay">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-ink transition-colors duration-300 hover:border-clay hover:text-clay">
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        {tab === "enquiries" ? (
          enquiries === null ? (
            <p className="text-sm text-ink/50">Loading enquiries…</p>
          ) : enquiries.length === 0 ? (
            <p data-testid="admin-empty" className="text-sm text-ink/50">No enquiries yet.</p>
          ) : (
            <div data-testid="admin-enquiry-list" className="flex flex-col gap-5">
              {enquiries.map((q) => (
                <article key={q.id} data-testid={`admin-enquiry-${q.id}`} className="border border-ink/10 bg-white p-7">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="font-serif text-2xl tracking-tight text-ink">{q.name}</p>
                      <p className="mt-1 text-xs uppercase tracking-[0.16em] text-clay">{q.garment_type} · {q.quantity}</p>
                    </div>
                    <p className="text-xs uppercase tracking-[0.14em] text-ink/40">{new Date(q.created_at).toLocaleString()}</p>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-ink/70 sm:grid-cols-2">
                    <p><span className="text-ink/40">Email:</span> <a className="hover:text-clay" href={`mailto:${q.email}`}>{q.email}</a></p>
                    <p><span className="text-ink/40">Phone:</span> {q.phone}</p>
                    {q.business && <p><span className="text-ink/40">Business:</span> {q.business}</p>}
                    {q.timeline && <p><span className="text-ink/40">Deadline:</span> {q.timeline}</p>}
                  </div>
                  {q.message && <p className="mt-4 border-t border-ink/10 pt-4 text-sm leading-relaxed text-ink/70">{q.message}</p>}
                  {q.attachments?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2 border-t border-ink/10 pt-4">
                      {q.attachments.map((a) => (
                        <a key={a.path} href={`${API}/files/${a.path}?auth=${token}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-attachment-${a.filename}`} className="inline-flex items-center gap-2 rounded-full border border-clay/40 px-4 py-2 text-xs font-semibold text-clay transition-colors duration-300 hover:bg-clay hover:text-bone">
                          <Paperclip className="h-3.5 w-3.5" />
                          {a.filename}
                        </a>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          )
        ) : projects === null ? (
          <p className="text-sm text-ink/50">Loading garments…</p>
        ) : (
          <div data-testid="admin-project-list" className="flex flex-col gap-5">
            <button onClick={addProject} data-testid="admin-project-add" className="inline-flex w-fit items-center gap-2 rounded-full border border-dashed border-ink/30 px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ink/70 transition-colors duration-300 hover:border-clay hover:text-clay">
              <Plus className="h-4 w-4" />
              Add garment
            </button>
            {projects.map((p) => (
              <ProjectRow key={p.id} project={p} token={token} onSaved={() => load(token)} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
